// components/TourPrompt.jsx — Anamoria SPA
// v1.2 — Jul 5, 2026 — Pass the dismiss response up (onDismiss(result)) so a
//   dismissal that flips onboarding_complete syncs into appState.
// v1.1 — Jul 1, 2026 — Present as a centered modal overlay, not an inline card.
//   Previously rendered inline at the top of the feed; now it sits in a
//   full-screen dimmed overlay so it reads as a deliberate welcome prompt.
//   Backdrop click dismisses (same as "Not now"). Content/logic unchanged.
//
// v1.0 — Welcome prompt card for app walkthrough (May 22, 2026)
//
// Renders a card at the top of the memory feed: "Want a quick tour?"
// with "Show me" and "Not now" buttons. Shown to users who haven't
// completed onboarding (onboardingComplete === false in appState).
//
// "Show me" → triggers onStart callback (parent starts tour)
// "Not now"  → calls POST /pilot/onboarding/dismiss, triggers onDismiss
//              After 3 dismissals, server sets onboarding_complete = true
//              (BP-7 prompt fatigue prevention).
//
// Props:
//   visible    — boolean (controlled by SpacePage)
//   onStart    — callback when user taps "Show me"
//   onDismiss  — callback when user taps "Not now"
//   getApi     — API client factory for POST /pilot/onboarding/dismiss
//
// Best practice sources:
//   Pendo (2025): user-initiated onboarding, not auto-start
//   Userpilot (2026): dismissible prompts with fatigue prevention
//   NextNative (2025): contextual, non-intrusive prompt placement

import { useState } from 'react';
import styles from './TourPrompt.module.css';

export default function TourPrompt({ visible, onStart, onDismiss, getApi }) {
  const [dismissing, setDismissing] = useState(false);
  const [exiting, setExiting] = useState(false);

  if (!visible || exiting) return null;

  async function handleDismiss() {
    if (dismissing) return;
    setDismissing(true);

    let result;
    try {
      const api = getApi();
      result = await api.post('/pilot/onboarding/dismiss');
    } catch (err) {
      // Non-blocking — dismiss is UX, not critical.
      // Prompt will reappear next session if server call fails.
      console.warn('Failed to persist tour dismiss:', err.message);
    }

    // Animate out, then notify parent (pass the dismiss result so a flag flip syncs)
    setExiting(true);
    setTimeout(() => {
      onDismiss(result);
    }, 200);
  }

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) handleDismiss();
  };

  return (
    <div
      className={styles.overlay}
      role="dialog"
      aria-modal="true"
      aria-label="App tour invitation"
      onClick={handleBackdropClick}
    >
      <div className={styles.promptCard}>
      <div className={styles.promptAccent} aria-hidden="true" />
      <div className={styles.promptBody}>
        <h3 className={styles.promptTitle}>Want a quick tour?</h3>
        <p className={styles.promptText}>
          We'll show you around in under a minute.
        </p>
        <div className={styles.promptActions}>
          <button
            className={styles.showMeBtn}
            onClick={onStart}
            type="button"
          >
            Show me
          </button>
          <button
            className={styles.notNowBtn}
            onClick={handleDismiss}
            disabled={dismissing}
            type="button"
          >
            Not now
          </button>
        </div>
      </div>
      </div>
    </div>
  );
}
